// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../utils/apiUrl';

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
	const [devMailMode, setDevMailMode] = useState(() => {
		try {
			return localStorage.getItem('devMailMode') === 'true';
		} catch (error) {
			return false;
		}
	});
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [userRole, setUserRole] = useState('');
	const [user, setUser] = useState(null);
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		try {
			localStorage.setItem('devMailMode', String(devMailMode));
		} catch (error) {
			// Ignore storage errors (private mode, quota, etc.)
		}
	}, [devMailMode]);

	// Check authentication status on mount
	useEffect(() => {
		checkAuthStatus();
	}, []);

	const checkAuthStatus = async () => {
		try {
			const response = await axios.get(buildApiUrl('auth/check'), {
				withCredentials: true,
			});

			if (response.data.isAuthenticated) {
				setIsLoggedIn(true);
				setUserRole(response.data.user.role);
				setUser(response.data.user);
			} else {
				// Just set state, don't call logout (prevents infinite loop)
				setIsLoggedIn(false);
				setUserRole('');
				setUser(null);
			}
		} catch (error) {
			// Not authenticated - just set state
			setIsLoggedIn(false);
			setUserRole('');
			setUser(null);
		} finally {
			setIsLoading(false);
		}
	};

	const login = async (loginData) => {
		try {
			const response = await axios.post(buildApiUrl('auth/login'), loginData, {
				withCredentials: true,
			});

			setIsLoggedIn(true);
			setUserRole(response.data.user.role);
			setUser(response.data.user);

			return { success: true, user: response.data.user };
		} catch (error) {
			return {
				success: false,
				error: error.response?.data?.message || 'Login failed',
			};
		}
	};

	const logout = async () => {
		try {
			await axios.post(buildApiUrl('auth/logout'), {}, { withCredentials: true });
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			setIsLoggedIn(false);
			setUserRole('');
			setUser(null);
		}
	};

	const refreshToken = async () => {
		try {
			await axios.post(buildApiUrl('auth/refresh'), {}, { withCredentials: true });
			return true;
		} catch (error) {
			console.error('Token refresh failed:', error);
			// Don't auto-logout here - let the interceptor handle it
			return false;
		}
	};

	return (
		<AuthContext.Provider
			value={{
				isLoggedIn,
				setIsLoggedIn,
				userRole,
				setUserRole,
				user,
				setUser,
				devMailMode,
				setDevMailMode,
				isLoading,
				login,
				logout,
				refreshToken,
				checkAuthStatus,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};
